import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Organizer } from '../../../types/organizer';
import { useLanguage } from '../../../contexts/LanguageContext'; 
import { Flag } from '../../common/Flag';
import { COUNTRY_CODES } from '../../../config/constants';

type Props = {
  onSubmit: (data: Partial<Organizer>) => void;
  initialData?: Partial<Organizer>;
  isLoading?: boolean;
  isEditing?: boolean;
};

export const OrganizerForm = ({
  onSubmit,
  initialData = {},
  isLoading,
  isEditing,
}: Props) => {
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    email: initialData.email || '',
    password: initialData.password || '',
    confirmPassword: '',
    country: initialData.country || '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { translations } = useLanguage(); 

  // Available countries list with codes
  const countries = Object.entries(COUNTRY_CODES).map(([code, name]) => ({
    code, name
  })).sort((a, b) => a.name.localeCompare(b.name));

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name =
        translations?.organizerForm?.validation?.required ||
        'This field is required';
    }

    if (!isEditing) {
      if (!formData.email.trim()) {
        newErrors.email =
          translations?.organizerForm?.validation?.required ||
          'This field is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email =
          translations?.organizerForm?.validation?.emailInvalid ||
          'Invalid email address';
      }

      if (!formData.password) {
        newErrors.password =
          translations?.organizerForm?.validation?.required ||
          'This field is required';
      } else if (formData.password.length < 6) {
        newErrors.password =
          translations?.organizerForm?.validation?.passwordLength ||
          'Password must be at least 6 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword =
          translations?.organizerForm?.validation?.passwordsNotMatch ||
          'Passwords do not match';
      }
    }
    
    if (isEditing && !formData.password) {
      newErrors.password =
        translations?.organizerForm?.validation?.required ||
        'This field is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const { confirmPassword, ...submitData } = formData;
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {translations?.organizerForm?.fields?.name || 'Name'}
          <span className="text-red-500 mr-1">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      {!isEditing && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.organizerForm?.fields?.email || 'Email'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.organizerForm?.fields?.password || 'Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={6}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.organizerForm?.fields?.confirmPassword ||
                'Confirm Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
              className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={6}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-500">
                {errors.confirmPassword}
              </p>
            )}
          </div>
        </>
      )}

      {isEditing && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.organizerForm?.fields?.password || 'Password'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={
                  translations?.organizerForm?.placeholders?.password ||
                  'Password is required'
                }
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password}</p>
            )}
          </div>
        </>
      )}
      
      {/* Country Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {translations?.organizerForm?.fields?.country || 'Country'}
        </label>
        <div className="relative">
          <select
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            className="w-full px-4 py-2 pl-12 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 border-gray-300"
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            {formData.country && <Flag countryCode={formData.country} size={20} />}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={() => onSubmit({})}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          {translations?.organizerForm?.buttons?.cancel || 'Cancel'}
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className={`px-6 py-2 rounded-lg text-white transition-colors ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {isLoading
            ? translations?.organizerForm?.buttons?.saving || 'Saving...'
            : translations?.organizerForm?.buttons?.save || 'Save'}
        </button>
      </div>
    </form>
  );
};
